import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PaymentRegistration } from "./PaymentRegistration";
import { 
  AlertTriangle, Search, CreditCard, Calendar, Phone, Mail, 
  Users, Euro, Clock, Filter, ChevronDown, ChevronUp, Download, Loader2 
} from "lucide-react";
import { useStudentBilling } from "@/hooks/useFinanceData";
import { useExportData } from "@/hooks/useExportData";
import { format, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";

interface DebtorsTableProps {
  onRefresh?: () => void;
}

export const DebtorsTable = ({ onRefresh }: DebtorsTableProps) => {
  const { billing, loading, refetch } = useStudentBilling();
  const { exportDebtors, isExporting } = useExportData();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDaysOverdue, setSelectedDaysOverdue] = useState("all");
  const [selectedBills, setSelectedBills] = useState<string[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBillForPayment, setSelectedBillForPayment] = useState<any>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'due_date',
    direction: 'asc'
  });

  // Filtrar solo facturas pendientes y vencidas
  const debtorBills = billing.filter(bill => 
    bill.status === 'pending' || bill.status === 'overdue'
  );

  // Aplicar filtros
  const filteredBills = debtorBills.filter(bill => {
    const matchesSearch = searchTerm === '' || 
      bill.student?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.student?.class.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.activity?.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = selectedStatus === 'all' || bill.status === selectedStatus;

    const daysOverdue = differenceInDays(new Date(), new Date(bill.due_date));
    const matchesDaysOverdue = selectedDaysOverdue === 'all' || 
      (selectedDaysOverdue === '0-30' && daysOverdue >= 0 && daysOverdue <= 30) ||
      (selectedDaysOverdue === '31-60' && daysOverdue >= 31 && daysOverdue <= 60) ||
      (selectedDaysOverdue === '61+' && daysOverdue >= 61);

    return matchesSearch && matchesStatus && matchesDaysOverdue;
  });

  // Ordenar facturas
  const sortedBills = [...filteredBills].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortConfig.key) {
      case 'student_name':
        aValue = a.student?.name || '';
        bValue = b.student?.name || '';
        break;
      case 'total_amount':
        aValue = a.total_amount;
        bValue = b.total_amount;
        break;
      case 'due_date':
        aValue = new Date(a.due_date);
        bValue = new Date(b.due_date);
        break;
      case 'days_overdue':
        aValue = differenceInDays(new Date(), new Date(a.due_date));
        bValue = differenceInDays(new Date(), new Date(b.due_date));
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedBills(sortedBills.map(bill => bill.id));
    } else {
      setSelectedBills([]);
    }
  };

  const handleSelectBill = (billId: string, checked: boolean) => {
    if (checked) {
      setSelectedBills(prev => [...prev, billId]);
    } else {
      setSelectedBills(prev => prev.filter(id => id !== billId));
    }
  };

  const handlePaymentSuccess = () => {
    refetch();
    onRefresh?.();
    setSelectedBills([]);
  };

  const handleExport = () => {
    const debtorsData = sortedBills.map(bill => ({
      id: bill.id,
      student_name: bill.student?.name || '',
      student_class: bill.student?.class || '',
      activity_name: bill.activity?.name || '',
      total_amount: bill.total_amount,
      due_date: bill.due_date,
      days_overdue: differenceInDays(new Date(), new Date(bill.due_date)),
      status: bill.status
    }));
    
    exportDebtors(debtorsData, `deudores_${new Date().toISOString().split('T')[0]}`);
  };

  const getDaysOverdueColor = (daysOverdue: number) => {
    if (daysOverdue < 0) return 'text-muted-foreground';
    if (daysOverdue <= 7) return 'text-warning';
    if (daysOverdue <= 30) return 'text-orange-600';
    return 'text-destructive';
  };

  const getDaysOverdueText = (daysOverdue: number) => {
    if (daysOverdue < 0) return `${Math.abs(daysOverdue)} días restantes`;
    if (daysOverdue === 0) return 'Vence hoy';
    return `${daysOverdue} días vencido`;
  };

  const totalDebt = sortedBills.reduce((sum, bill) => sum + bill.total_amount, 0);
  const selectedDebt = sortedBills
    .filter(bill => selectedBills.includes(bill.id))
    .reduce((sum, bill) => sum + bill.total_amount, 0);

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6 text-center">
          <div className="text-muted-foreground">Cargando deudores...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Resumen de deudas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-destructive/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Deuda</p>
                <p className="text-lg font-bold text-destructive">{totalDebt.toFixed(2)}€</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-warning/10 rounded-lg">
                <Users className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Deudores</p>
                <p className="text-lg font-bold text-warning">{debtorBills.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Euro className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Seleccionado</p>
                <p className="text-lg font-bold text-primary">{selectedDebt.toFixed(2)}€</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-primary" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Estudiante, clase o actividad..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Estado</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="overdue">Vencido</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Días vencimiento</label>
              <Select value={selectedDaysOverdue} onValueChange={setSelectedDaysOverdue}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="0-30">0-30 días</SelectItem>
                  <SelectItem value="31-60">31-60 días</SelectItem>
                  <SelectItem value="61+">Más de 60 días</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Acciones</label>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={selectedBills.length === 0}
                  className="flex-1"
                >
                  Enviar recordatorio
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de deudores */}
      <Card className="shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Deudores ({sortedBills.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedBills.length} seleccionados
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExport}
                disabled={isExporting || loading || sortedBills.length === 0}
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {isExporting ? 'Exportando...' : 'Exportar'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {sortedBills.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No hay deudores</p>
              <p className="text-sm">Todas las facturas están al día</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-3">
                      <Checkbox
                        checked={selectedBills.length === sortedBills.length}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="text-left py-3 px-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort('student_name')}
                        className="font-medium hover:bg-transparent p-0"
                      >
                        Estudiante
                        {sortConfig.key === 'student_name' && (
                          sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                        )}
                      </Button>
                    </th>
                    <th className="text-left py-3 px-3">Actividad</th>
                    <th className="text-left py-3 px-3">Tipo</th>
                    <th className="text-right py-3 px-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort('total_amount')}
                        className="font-medium hover:bg-transparent p-0"
                      >
                        Importe
                        {sortConfig.key === 'total_amount' && (
                          sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                        )}
                      </Button>
                    </th>
                    <th className="text-center py-3 px-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort('due_date')}
                        className="font-medium hover:bg-transparent p-0"
                      >
                        Vencimiento
                        {sortConfig.key === 'due_date' && (
                          sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                        )}
                      </Button>
                    </th>
                    <th className="text-center py-3 px-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort('days_overdue')}
                        className="font-medium hover:bg-transparent p-0"
                      >
                        Estado
                        {sortConfig.key === 'days_overdue' && (
                          sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                        )}
                      </Button>
                    </th>
                    <th className="text-center py-3 px-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedBills.map((bill) => {
                    const daysOverdue = differenceInDays(new Date(), new Date(bill.due_date));
                    return (
                      <tr key={bill.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-3">
                          <Checkbox
                            checked={selectedBills.includes(bill.id)}
                            onCheckedChange={(checked) => handleSelectBill(bill.id, checked as boolean)}
                          />
                        </td>
                        <td className="py-3 px-3">
                          <div>
                            <p className="font-medium">{bill.student?.name}</p>
                            <p className="text-sm text-muted-foreground">{bill.student?.class}</p>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <p className="text-sm">{bill.activity?.name}</p>
                        </td>
                        <td className="py-3 px-3">
                          <Badge 
                            variant="outline" 
                            className={bill.billing_type === 'per_session' ? 'border-blue-500 text-blue-700' : 'border-purple-500 text-purple-700'}
                          >
                            {bill.billing_type === 'per_session' ? 'Por Día' : 'Mensual'}
                          </Badge>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <p className="font-medium">{bill.total_amount.toFixed(2)}€</p>
                          <p className="text-sm text-muted-foreground">
                            {bill.sessions_attended} sesiones
                          </p>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <p className="text-sm">
                            {format(new Date(bill.due_date), 'dd/MM/yyyy', { locale: es })}
                          </p>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <Badge variant={bill.status === 'overdue' ? 'destructive' : 'secondary'}>
                              {bill.status === 'overdue' ? 'Vencido' : 'Pendiente'}
                            </Badge>
                            <p className={`text-xs ${getDaysOverdueColor(daysOverdue)}`}>
                              {getDaysOverdueText(daysOverdue)}
                            </p>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <div className="flex gap-1 justify-center">
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedBillForPayment(bill);
                                setShowPaymentModal(true);
                              }}
                              className="bg-success hover:bg-success/90"
                            >
                              <CreditCard className="w-3 h-3 mr-1" />
                              Pagar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de pago */}
      {selectedBillForPayment && (
        <PaymentRegistration
          bill={selectedBillForPayment}
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedBillForPayment(null);
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default DebtorsTable;